import { LinearProgress, makeStyles } from '@material-ui/core';
import { EmptyState } from '@backstage/core-components';
import { EntityDisplayName, useEntity } from '@backstage/plugin-catalog-react';
import {
  MetricKey,
  SONARQUBE_PROJECT_KEY_ANNOTATION,
} from '@backstage-community/plugin-sonarqube-react';
import React, { FC, useEffect, useState } from 'react';
import { productSecurityProps } from '../Checkmarx/types';

const useStyles = makeStyles({
  panel: {
    background: '#fff',
    border: '1px solid #dce1e8',
    boxShadow: '0 1px 3px rgba(5, 22, 77, 0.08)',
    color: '#172d62',
    marginTop: '12px',
    padding: '16px',
  },
  title: {
    color: '#05164d',
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '10px',
  },
  subtitle: {
    color: 'grey',
    marginBottom: '12px',
  },
  productName: {
    display: 'inline',
    fontWeight: 700,
    marginLeft: '4px',
    color: '#172d62',
  },
  portfolio: {
    alignItems: 'center',
    borderBottom: '1px solid #dce1e8',
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: '10px',
  },
  portfolioName: {
    alignItems: 'center',
    color: '#4263be',
    display: 'flex',
    fontSize: '22px',
    fontWeight: 700,
    gap: '4px',
  },
  portfolioLink: {
    color: 'inherit',
    textDecoration: 'none',
  },
  summary: {
    color: '#4f5b6d',
    fontSize: '13px',
    fontWeight: 700,
  },
  ratings: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(96px, 1fr))',
    paddingTop: '12px',
  },
  rating: {
    alignItems: 'center',
    color: '#526074',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '12px',
    gap: '7px',
    textAlign: 'center',
  },
  value: {
    color: '#69778d',
    fontSize: '11px',
  },
  grade: {
    alignItems: 'center',
    background: '#d9f5e1',
    borderRadius: '50%',
    color: '#17873a',
    display: 'flex',
    fontSize: '12px',
    fontWeight: 700,
    height: '28px',
    justifyContent: 'center',
    width: '28px',
  },
  gradeA: {
    background: '#d9f5e1',
    color: '#17873a',
  },
  gradeB: {
    background: '#e6f4dc',
    color: '#3f7d20',
  },
  gradeC: {
    background: '#fff0bd',
    color: '#886b00',
  },
  gradeD: {
    background: '#ffe0c2',
    color: '#a24c00',
  },
  gradeE: {
    background: '#ffd9d7',
    color: '#be2a32',
  },
});

type RatingMetric = {
  label: string;
  gradeMetric?: MetricKey;
  valueMetric: MetricKey;
  format: 'count' | 'percentage';
  gradeFormat?: 'rating' | 'percentage';
};

type PortfolioMetricKey = MetricKey | 'ncloc';
type ProjectMeasures = {
  projectKey: string;
  measures: Partial<Record<PortfolioMetricKey, number>>;
};

const ratings: readonly RatingMetric[] = [
  {
    label: 'Security',
    gradeMetric: 'security_rating',
    valueMetric: 'vulnerabilities',
    format: 'count',
  },
  {
    label: 'Reliability',
    gradeMetric: 'reliability_rating',
    valueMetric: 'bugs',
    format: 'count',
  },
  {
    label: 'Maintainability',
    gradeMetric: 'sqale_rating',
    valueMetric: 'code_smells',
    format: 'count',
  },
  {
    label: 'Hotspots Reviewed',
    gradeMetric: 'security_hotspots_reviewed',
    valueMetric: 'security_hotspots_reviewed',
    format: 'percentage',
    gradeFormat: 'percentage',
  },
  {
    label: 'Coverage',
    gradeMetric: 'coverage',
    valueMetric: 'coverage',
    format: 'percentage',
    gradeFormat: 'percentage',
  },
  {
    label: 'Duplications',
    gradeMetric: 'duplicated_lines_density',
    valueMetric: 'duplicated_lines_density',
    format: 'percentage',
    gradeFormat: 'percentage',
  },
];

const SonarQubeComponent: FC<{ data: productSecurityProps }> = ({ data }) => {
  const styles = useStyles();
  const [summaries, setSummaries] = useState<ProjectMeasures[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { entity } = useEntity();
  useEffect(() => {
    let isMounted = true;

    const loadSummaries = async () => {
      if (!data.catalogEntities) {
        return;
      }

      setIsLoading(true);
      try {
        const systemName = (data.appName || data.product.name)
          .split('system:default/')[1]
          ?.toLowerCase();
        const entities = (data.catalogEntities ?? []).filter(
          entity =>
            entity.kind === 'Component' &&
            entity.spec?.system === systemName &&
            Boolean(
              entity.metadata.annotations?.[SONARQUBE_PROJECT_KEY_ANNOTATION],
            ),
        );

        const projects = entities.map(entity => {
          const annotation =
            entity.metadata.annotations?.[SONARQUBE_PROJECT_KEY_ANNOTATION] ??
            '';
          const separatorIndex = annotation.indexOf('/');
          const projectKey =
            separatorIndex >= 0
              ? annotation.slice(separatorIndex + 1)
              : annotation;

          return { projectKey };
        });
        const fetchData = (window as any).fetchData;
        const urls = (window as any).urls;
        const results = (await fetchData(urls.sonarQubeMeasuresByProjects, {
          projects,
        })) as ProjectMeasures[];

        if (isMounted) {
          setSummaries(
            Array.isArray(results)
              ? results.filter(
                  result => Object.keys(result.measures).length > 0,
                )
              : [],
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSummaries();
    return () => {
      isMounted = false;
    };
  }, [data.appName, data.catalogEntities, data.product.name]);

  const getMetricValue = (
    metric: PortfolioMetricKey,
    format: RatingMetric['format'],
  ) => {
    const values = summaries
      .map(summary => Number(summary.measures[metric]))
      .filter(value => Number.isFinite(value) && value >= 0);
    const metricValue = values.length
      ? format === 'count'
        ? values.reduce((total, value) => total + value, 0)
        : values.reduce((total, value) => total + value, 0) / values.length
      : undefined;
    if (metricValue === undefined) {
      return '-';
    }

    return format === 'percentage'
      ? `${metricValue.toFixed(1)}%`
      : metricValue.toLocaleString();
  };

  const getGrade = (
    metric?: MetricKey,
    format: RatingMetric['gradeFormat'] = 'rating',
  ) => {
    if (!metric) return undefined;

    const values = summaries
      .map(summary => Number(summary.measures[metric]))
      .filter(
        value =>
          Number.isFinite(value) &&
          (format === 'percentage' ? value >= 0 : value > 0),
      );
    const rating = values.length ? Math.max(...values) : undefined;

    if (format === 'percentage') {
      if (rating === undefined) return '-';
      if (rating >= 80) return 'A';
      if (rating >= 60) return 'B';
      if (rating >= 40) return 'C';
      if (rating >= 20) return 'D';
      return 'E';
    }

    return rating ? String.fromCharCode(64 + rating) : '-';
  };

  const getGradeClass = (grade?: string) => {
    if (grade === 'A') return styles.gradeA;
    if (grade === 'B') return styles.gradeB;
    if (grade === 'C') return styles.gradeC;
    if (grade === 'D') return styles.gradeD;
    if (grade === 'E') return styles.gradeE;
    return undefined;
  };

  const projectCount = summaries.length;
  const linesOfCode = summaries.reduce(
    (total, summary) => total + (summary.measures.ncloc ?? 0),
    0,
  );
  const productName = (
    <a
      className={styles.productName}
      href={`https://build.cicd.prod.ddl.lhgroup.de/sonar/portfolio?id=${entity?.metadata?.name}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontWeight: 700, marginLeft: '4px' }}
    >
      <EntityDisplayName entityRef={data?.appName || data?.product.name} />
    </a>
  );

  return (
    <section className={styles.panel} aria-label="SonarQube portfolio status">
      <div className={styles.title}>Portfolio Status</div>
      <div className={styles.subtitle}>SonarQube · Portfolio {productName}</div>
      {isLoading && <LinearProgress />}
      {!isLoading && projectCount > 0 && (
        <>
          <div className={styles.portfolio}>
            <div className={styles.summary}>
              {linesOfCode.toLocaleString()} Lines of Code · {projectCount}{' '}
              Projects
            </div>
          </div>
          <div className={styles.ratings}>
            {ratings.map(rating => (
              <div className={styles.rating} key={rating.label}>
                {rating.gradeMetric &&
                  (() => {
                    const grade = getGrade(
                      rating.gradeMetric,
                      rating.gradeFormat,
                    );

                    return (
                      <span
                        className={`${styles.grade} ${getGradeClass(grade)}`}
                      >
                        {grade}
                      </span>
                    );
                  })()}
                {rating.label}
                <span className={styles.value}>
                  {getMetricValue(rating.valueMetric, rating.format)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      {!isLoading && projectCount === 0 && (
        <EmptyState missing="data" title="No data from SonarQube." />
      )}
    </section>
  );
};

export { SonarQubeComponent };
